import type { EmployeeSnapshot, Session } from '@klukka/client';
import {
  dow,
  fmtDate,
  fmtH,
  isLangCode,
  isOverlong,
  maxShiftHours,
  t,
  type DayRow,
  type LangCode,
} from '@klukka/core';
import { ErrorBanner, Ic, Spinner, useApiResource } from '@klukka/ui';
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { clearCachedData, watchConnectivity } from './pwa';
import { deriveEmployeeView, totalHours } from './derive';
import { Login } from './Login';
import { EditHoursSheet, LanguageSheet, RequestSheet, SignSheet } from './sheets';
import { HomeTab } from './tabs/HomeTab';
import { PeriodTab } from './tabs/PeriodTab';
import { ProfileTab } from './tabs/ProfileTab';
import { ReportTab } from './tabs/ReportTab';
import { RequestsTab } from './tabs/RequestsTab';

type Tab = 'home' | 'period' | 'report' | 'requests' | 'profile';

const TABS: Tab[] = ['home', 'period', 'report', 'requests', 'profile'];

/**
 * Where an incoming link wants to land.
 *
 * `?tab=report&slip=<periodKey>` is what a payslip notification carries. An
 * installed portal opens it in the app because the manifest scopes the whole
 * origin; everyone else gets a browser tab, which is the same screen. The
 * manifest's own "Vinnuskýrsla" shortcut uses the same parameter and had
 * nothing reading it until now.
 *
 * The query string is stripped afterwards so a reload does not keep dragging
 * the reader back to a payslip they have already looked at.
 */
function openingTab(): Tab {
  const wanted = new URLSearchParams(window.location.search).get('tab');
  return TABS.includes(wanted as Tab) ? (wanted as Tab) : 'home';
}

function openingSlip(): string | null {
  const key = new URLSearchParams(window.location.search).get('slip');
  // Same shape the rest of the app uses for a period, and validated rather
  // than trusted: it comes from a URL and is rendered into the page.
  return key && /^\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
}
const LANG_KEY = 'klukka.lang';

/** Re-render once a second while the clock is running, so the timer ticks. */
function useTick(active: boolean) {
  const [, bump] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => bump((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => watchConnectivity(setOnline), []);
  const [lang, setLang] = useState<LangCode>(() => {
    const saved = window.localStorage.getItem(LANG_KEY);
    return saved && isLangCode(saved) ? saved : 'is';
  });

  useEffect(() => {
    void api.currentSession().then((s) => {
      setSession(s);
      setChecking(false);
    });
  }, []);

  const setLangPersisted = useCallback((l: LangCode) => {
    window.localStorage.setItem(LANG_KEY, l);
    setLang(l);
  }, []);

  if (checking) return <div className="app-shell"><Spinner label="…" /></div>;

  if (!session) {
    return (
      <>
        <OfflineBar lang={lang} online={online} />
        <Login lang={lang} onLang={setLangPersisted} onSignedIn={setSession} />
      </>
    );
  }

  return (
    <>
      <OfflineBar lang={lang} online={online} />
      <SignedIn
        key={session.token}
        lang={lang}
        online={online}
        onLang={setLangPersisted}
        onSignedOut={() => setSession(null)}
      />
    </>
  );
}

/**
 * A thin strip rather than a dialog. Losing signal on a site is normal and
 * the app still works from cache — it just must not pretend the numbers are
 * live.
 */
function OfflineBar({ lang, online }: { lang: LangCode; online: boolean }) {
  if (online) return null;
  return (
    <div className="offline-bar" role="status">
      <Ic.alert size={13} /> {t(lang, 'offlineBanner')}
    </div>
  );
}

function SignedIn({ lang, online, onLang, onSignedOut }: {
  lang: LangCode;
  online: boolean;
  onLang: (l: LangCode) => void;
  onSignedOut: () => void;
}) {
  const load = useCallback(() => api.getEmployeeSnapshot(), []);
  const res = useApiResource<EmployeeSnapshot>(load);
  const [tab, setTab] = useState<Tab>(() => openingTab());
  // Set once from the URL and kept until the employee navigates away, so the
  // payslip an emailed link points at can be scrolled to and marked.
  const [slip, setSlip] = useState<string | null>(() => openingSlip());
  const [offset, setOffset] = useState(0);
  const [langOpen, setLangOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [editDay, setEditDay] = useState<DayRow | null>(null);

  const snap = res.data;
  const running = !!snap?.shift && !snap.shift.clockOutAt;

  // Once they leave the report the link has done its job; keeping the mark
  // would make a later visit look like a fresh notification.
  useEffect(() => {
    if (tab !== 'report' && slip) setSlip(null);
  }, [tab, slip]);
  useTick(running);

  // Coming back into signal should replace the cached snapshot, not wait for
  // the worker to tick.
  useEffect(() => {
    if (online) void res.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // The employee's own language wins once we know who they are.
  useEffect(() => {
    if (snap && snap.employee.lang !== lang) onLang(snap.employee.lang);
    // Only react to the profile's language changing, not to local switching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap?.employee.lang]);

  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);

  if (res.loading && !snap) return <div className="app-shell"><Spinner label={tr('loading')} /></div>;

  if (!snap) {
    return (
      <div className="app-shell">
        <div className="ph-app"><div className="ph-body">
          <ErrorBanner message={tr('offline')} onRetry={() => void res.reload()} retryLabel={tr('retry')} />
        </div></div>
      </div>
    );
  }

  const view = deriveEmployeeView(snap, offset);
  const notification = snap.notifications.find((n) => n.kind === 'overtime_reminder');
  const closedNotice = snap.notifications.find((n) => n.kind === 'shift_closed');
  const maxHours = maxShiftHours(snap.company.settings);
  // The server closes these on its next read; until then, don't offer a
  // confirmation it would refuse.
  const overMax = running && isOverlong(snap.shift!, snap.company.settings);

  const pickLanguage = async (l: LangCode) => {
    onLang(l);
    setLangOpen(false);
    await res.mutate(() => api.updateMyProfile({ lang: l })).catch(() => undefined);
  };

  const toggleClock = () => {
    void res.mutate(() => (running ? api.clockOut() : api.clockIn())).catch(() => undefined);
  };

  const todayStatus = (() => {
    const row = view.todayRow;
    const covering = snap.requests.find(
      (r) => r.status === 'approved' && r.from <= snap.serverToday && r.to >= snap.serverToday,
    );
    if (row?.sick || covering?.type === 'sick') return { key: 'sick', color: 'var(--warn)' } as const;
    if (row?.vacation || covering?.type === 'vacation') return { key: 'vacation', color: 'var(--ok)' } as const;
    if (covering?.type === 'leave') return { key: 'leave', color: 'var(--ok)' } as const;
    if (row?.holiday) return { key: 'holiday', color: 'var(--ot2)' } as const;
    if (row?.weekend) return { key: 'weekend', color: 'var(--ot2)' } as const;
    return { key: 'workday', color: 'var(--primary)' } as const;
  })();

  const navItems: Array<[Tab, (p: { size?: number }) => JSX.Element, string]> = [
    ['home', Ic.clock, tr('home')],
    ['period', Ic.calendar, tr('period')],
    ['report', Ic.doc, tr('reportTab')],
    ['requests', Ic.inbox, tr('requests')],
    ['profile', Ic.user, tr('profileTab')],
  ];

  return (
    <div className="app-shell">
      <div className="ph-app">
        <div className="ph-head">
          <div>
            <div className="ph-hi">{tr('hi')}, {snap.employee.name.split(' ')[0]}</div>
            <div className="ph-date">
              <span>
                {tr('today')} · {fmtDate(snap.serverToday)} ({dow(lang, new Date(`${snap.serverToday}T12:00`).getDay())})
              </span>
              <span className="day-status" style={{ borderColor: todayStatus.color, color: todayStatus.color }}>
                <i style={{ background: todayStatus.color }} />{tr(todayStatus.key)}
              </span>
            </div>
          </div>
          <button className="lang-btn" onClick={() => setLangOpen(true)} aria-label={tr('language')}>
            <Ic.globe size={15} /> {lang.toUpperCase()}
          </button>
        </div>

        {res.error ? (
          <div style={{ padding: '10px 16px 0' }}>
            <ErrorBanner message={tr('saveFailed')} onRetry={() => { res.clearError(); void res.reload(); }} retryLabel={tr('retry')} />
          </div>
        ) : null}

        {tab === 'home' ? (
          <HomeTab snap={snap} view={view} lang={lang} busy={res.busy} onToggleClock={toggleClock} />
        ) : null}
        {tab === 'period' ? (
          <PeriodTab snap={snap} view={view} lang={lang} offset={offset} onOffset={setOffset} onEditDay={setEditDay} />
        ) : null}
        {tab === 'report' ? (
          <ReportTab
            snap={snap}
            view={view}
            lang={lang}
            offset={offset}
            onOffset={setOffset}
            onSign={() => setSignOpen(true)}
            highlightSlip={slip}
          />
        ) : null}
        {tab === 'requests' ? (
          <RequestsTab snap={snap} lang={lang} onNew={() => setReqOpen(true)} />
        ) : null}
        {tab === 'profile' ? (
          <ProfileTab
            snap={snap}
            lang={lang}
            busy={res.busy}
            onSave={async (patch) => { await res.mutate(() => api.updateMyProfile(patch)); }}
            onLogout={() => {
              void api.logout()
                .then(clearCachedData)
                .then(onSignedOut);
            }}
          />
        ) : null}

        <div className="ph-nav">
          {navItems.map(([key, Icon, label]) => (
            <button key={key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
              <Icon size={19} />{label}
            </button>
          ))}
        </div>

        {/*
          Past the standard workday the app asks once: are you done, or is this
          overtime? Confirming stamps the shift and leaves an audit entry the
          employer sees — nobody has to remember the conversation later.
        */}
        {overMax ? (
          <div className="notif" role="alert">
            <div className="notif-head">
              <Ic.clock size={16} /> <b>{tr('overMaxTitle')}</b>
              <span className="dimtx" style={{ marginLeft: 'auto', fontSize: 11 }}>Klukka</span>
            </div>
            <div className="notif-body">
              {tr('overMaxBody').replace('{max}', fmtH(maxHours))}
            </div>
            <div className="notif-btns">
              <button
                className="btn-main"
                style={{ background: 'var(--live)', borderColor: 'var(--live)', flex: 1, padding: '10px 8px' }}
                onClick={toggleClock}
              >
                <Ic.stop size={15} /> {tr('clockOut')}
              </button>
            </div>
          </div>
        ) : notification ? (
          <div className="notif" role="alert">
            <div className="notif-head">
              <Ic.clock size={16} /> <b>{tr('notifTitle')}</b>
              <span className="dimtx" style={{ marginLeft: 'auto', fontSize: 11 }}>Klukka</span>
            </div>
            <div className="notif-body">
              {tr('notifBody').replace('{h}', fmtH(Number(notification.payload.hours ?? view.live)))}
            </div>
            <div className="notif-btns">
              <button
                className="btn-main"
                style={{ background: 'var(--live)', borderColor: 'var(--live)', flex: 1, padding: '10px 8px' }}
                onClick={toggleClock}
              >
                <Ic.stop size={15} /> {tr('clockOut')}
              </button>
              <button
                className="btn-ghost"
                style={{ flex: 1, padding: '10px 8px' }}
                onClick={() => { void res.mutate(() => api.confirmOvertime()).catch(() => undefined); }}
              >
                {tr('stillWorking')}
              </button>
            </div>
          </div>
        ) : closedNotice ? (
          <div className="notif" role="status">
            <div className="notif-head">
              <Ic.clock size={16} /> <b>{tr('shiftClosedTitle')}</b>
              <span className="dimtx" style={{ marginLeft: 'auto', fontSize: 11 }}>Klukka</span>
            </div>
            <div className="notif-body">
              {tr('shiftClosedBody')
                .replace('{date}', fmtDate(String(closedNotice.payload.date ?? '')))
                .replace('{max}', fmtH(Number(closedNotice.payload.maxHours ?? maxHours)))}
            </div>
            <div className="notif-btns">
              <button
                className="btn-ghost"
                style={{ flex: 1, padding: '10px 8px' }}
                onClick={() => { void res.mutate(() => api.acknowledgeNotification(closedNotice.id)).catch(() => undefined); }}
              >
                {tr('okBtn')}
              </button>
            </div>
          </div>
        ) : null}

        {langOpen ? (
          <LanguageSheet lang={lang} tr={tr} onPick={(l) => void pickLanguage(l)} onClose={() => setLangOpen(false)} />
        ) : null}

        {signOpen ? (
          <SignSheet
            tr={tr}
            name={snap.employee.name}
            hours={totalHours(view.sel.calc)}
            periodLabel={`${tr('period')} ${view.sel.pinfo.start} → ${view.sel.pinfo.end}`}
            busy={res.busy}
            onSubmit={(signature) => {
              void res.mutate(() =>
                api.signTimesheet({
                  periodKey: view.sel.key,
                  label: `${tr('period')} ${view.sel.pinfo.start} → ${view.sel.pinfo.end}`,
                  hours: Math.round(totalHours(view.sel.calc) * 100) / 100,
                  signature,
                }),
              ).then(() => setSignOpen(false)).catch(() => undefined);
            }}
            onClose={() => setSignOpen(false)}
          />
        ) : null}

        {reqOpen ? (
          <RequestSheet
            tr={tr}
            today={snap.serverToday}
            busy={res.busy}
            onSubmit={(input) => {
              void res.mutate(() => api.createRequest(input)).then(() => setReqOpen(false)).catch(() => undefined);
            }}
            onClose={() => setReqOpen(false)}
          />
        ) : null}

        {editDay ? (
          <EditHoursSheet
            tr={tr}
            day={editDay}
            busy={res.busy}
            onSave={(hours) => {
              void res.mutate(() => api.editMyHours(editDay.date, hours))
                .then(() => setEditDay(null))
                .catch(() => undefined);
            }}
            onClose={() => setEditDay(null)}
          />
        ) : null}
      </div>
    </div>
  );
}
